import React from 'react';

export interface CardProps extends React.ComponentProps<'div'> {
  variant?: 'default' | 'outlined' | 'elevated' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}

export interface CardHeaderProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

export interface CardBodyProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

export interface CardFooterProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
}

const Card = React.forwardRef<React.ElementRef<'div'>, CardProps>(
  ({ variant = 'default', padding = 'md', className = '', children, ...props }, ref) => {
    const baseClasses = 'rounded-lg transition-shadow';

    const variantClasses = {
      default: 'bg-white dark:bg-xtnd-dark-800 border border-xtnd-dark-100 dark:border-xtnd-dark-700',
      outlined: 'bg-white dark:bg-xtnd-dark-800 border border-xtnd-dark-200 dark:border-xtnd-dark-600',
      elevated: 'bg-white dark:bg-xtnd-dark-800 shadow-lg border border-xtnd-dark-100 dark:border-xtnd-dark-700',
      filled: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-700 border border-xtnd-dark-100 dark:border-xtnd-dark-600',
    };

    const paddingClasses = {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
      xl: 'p-8',
    };

    const classes =
      `${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

export const CardHeader = React.forwardRef<React.ElementRef<'div'>, CardHeaderProps>(
  ({ className = '', children, ...props }, ref) => {
    const classes = `border-b border-xtnd-dark-100 dark:border-xtnd-dark-700 pb-4 mb-4 ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

export const CardBody = React.forwardRef<React.ElementRef<'div'>, CardBodyProps>(
  ({ className = '', children, ...props }, ref) => {
    const classes = `flex-1 ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

export const CardFooter = React.forwardRef<React.ElementRef<'div'>, CardFooterProps>(
  ({ className = '', children, ...props }, ref) => {
    const classes = `border-t border-xtnd-dark-100 dark:border-xtnd-dark-700 pt-4 mt-4 ${className}`.trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardBody.displayName = 'CardBody';
CardFooter.displayName = 'CardFooter';

export default Card;
